// Modal for incident reporting on desktop
// CHANGE: Removed all debug console statements for production
// Uses the reusable Modal component
// Displays the same form as BottomSheet but in modal format
// Updated: Removed urgency level, made category optional
// Added site selection for multi-site users
// Updated: Changed category grid to 2 columns on all screen sizes for consistent layout
// Updated: Reorganized fields - Site (for multi-site users), then category, then description, then attachments
// Updated: Added file attachment support (images and PDFs, max 5 files, 5 MB per file)
// UPDATED: Removed UserX and Star icons (vestiges hr/quality categories)
// UX IMPROVEMENTS:
// - Now uses useIncidentForm hook for shared logic
// - Aligned colors with design system (primary-600 instead of red/orange gradient)
// - Removed AlertTriangle icon from submit button
// - Category selection uses primary-500/primary-50 colors
// - Added dirty form detection with close confirmation
// - File attachments with preview grid

import { useState } from 'react';
import { Users, PackageX, Wrench, HelpCircle, AlertTriangle, MapPin, Send } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { INCIDENT_CATEGORIES } from '../../lib/incidents';
import { BouncingDotsLoader } from '../ui/BouncingDotsLoader';
import { CustomSelect } from '../ui/CustomSelect';
import { useIncidentForm, MAX_DESCRIPTION_LENGTH } from '../../hooks/useIncidentForm';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { FileAttachmentInput } from '../ui/FileAttachmentInput';

interface IncidentFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ICON_MAP = {
  Users,
  PackageX,
  Wrench,
  HelpCircle
} as const;

export function IncidentFormModal({ isOpen, onClose, onSuccess }: IncidentFormModalProps) {
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  const {
    selectedCategory,
    setSelectedCategory,
    description,
    setDescription,
    isDescriptionFocused,
    setIsDescriptionFocused,
    userSites,
    selectedSiteId,
    setSelectedSiteId,
    attachedFiles,
    setAttachedFiles,
    isLoadingSites,
    isSubmitting,
    error,
    canSubmit,
    isDirty,
    handleSubmit,
  } = useIncidentForm({
    isOpen,
    onSuccess: () => {
      onClose();
      onSuccess();
    },
  });

  const handleCloseAttempt = () => {
    if (isDirty) {
      setShowCloseConfirmation(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowCloseConfirmation(false);
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleCloseAttempt}
        title="Signaler un incident"
        size="lg"
        closeOnOverlayClick={!isSubmitting}
        closeOnEscape={!isSubmitting}
      >
      <form onSubmit={handleSubmit} className="flex flex-col max-h-[70vh]">
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
              <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Show loader while loading sites */}
          {isLoadingSites ? (
            <div className="flex items-center justify-center min-h-[300px]">
              <BouncingDotsLoader size="lg" />
            </div>
          ) : (
            <>
              {/* Site Selection - Show only if user has multiple sites, shown first */}
          {userSites.length > 1 && (
            <div>
              <label htmlFor="site-select" className="block text-sm font-semibold text-gray-900 mb-2">
                Site <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" />
                <CustomSelect
                  value={selectedSiteId}
                  onChange={setSelectedSiteId}
                  options={userSites.map(site => ({
                    value: site.id,
                    label: site.name
                  }))}
                  disabled={isSubmitting || isLoadingSites}
                  placeholder="Sélectionnez un site"
                  className="pl-10"
                />
              </div>
            </div>
          )}

          {/* Category Selection - Optional, shown second */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              Thématique <span className="text-xs text-gray-500 font-normal">(optionnel)</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              {INCIDENT_CATEGORIES.map((category) => {
                const Icon = ICON_MAP[category.icon as keyof typeof ICON_MAP];
                const isSelected = selectedCategory === category.value;

                return (
                  <button
                    key={category.value}
                    type="button"
                    onClick={() => setSelectedCategory(isSelected ? null : category.value)}
                    disabled={isSubmitting}
                    className={`p-4 rounded-xl border-2 transition-all duration-200 text-center ${
                      isSelected
                        ? 'border-primary-400 bg-primary-50 shadow-md'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Icon size={28} className={`mx-auto ${isSelected ? 'text-primary-600 mb-2' : 'text-gray-600 mb-2'}`} />
                    <div className={`font-semibold text-sm ${isSelected ? 'text-primary-900' : 'text-gray-900'}`}>
                      {category.label}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {category.description}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Description - Shown third */}
          <div>
            <label htmlFor="description-modal" className="block text-sm font-semibold text-gray-900 mb-2">
              Description du problème <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description-modal"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onFocus={() => setIsDescriptionFocused(true)}
              onBlur={() => setIsDescriptionFocused(false)}
              placeholder="Décrivez le problème rencontré de manière claire et précise..."
              disabled={isSubmitting}
              maxLength={MAX_DESCRIPTION_LENGTH}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all resize-none disabled:bg-gray-50 disabled:cursor-not-allowed"
              rows={4}
              required
            />
            {description.length > MAX_DESCRIPTION_LENGTH * 0.8 && (
              <div className={`text-xs mt-1 ${
                description.length > MAX_DESCRIPTION_LENGTH * 0.9
                  ? 'text-orange-600 font-medium'
                  : 'text-gray-500'
              }`}>
                {description.length} / {MAX_DESCRIPTION_LENGTH} caractères
              </div>
            )}
          </div>

          {/* File Attachments - Shown fourth */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Pièces jointes <span className="text-xs text-gray-500 font-normal">(optionnel)</span>
            </label>
            <FileAttachmentInput
              files={attachedFiles}
              onFilesChange={setAttachedFiles}
              disabled={isSubmitting}
              isMobile={false}
            />
          </div>
            </>
          )}
        </div>

        {/* Fixed Footer with Actions */}
        <div className="shrink-0 flex gap-3 px-6 pb-6 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={handleCloseAttempt}
            disabled={isSubmitting}
            className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-all duration-200 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
              canSubmit
                ? 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Envoi...</span>
              </>
            ) : (
              <>
                <Send size={20} />
                <span>Envoyer</span>
              </>
            )}
          </button>
        </div>
      </form>
      </Modal>

      {/* Close Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showCloseConfirmation}
        onCancel={() => setShowCloseConfirmation(false)}
        onConfirm={handleConfirmClose}
        title="Modifications non enregistrées"
        message="Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter sans enregistrer ?"
        confirmLabel="Quitter sans enregistrer"
        cancelLabel="Continuer à modifier"
        variant="warning"
      />
    </>
  );
}
